import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { HandCoins, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";

export function DonationClubView({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-5">
      <div className="lt-card p-6">
        <div className="overline">User</div>
        <div className="mt-2 grid md:grid-cols-5 gap-4">
          <div>
            <div className="text-xs text-neutral-500">ID</div>
            <div className="font-display text-2xl tabular" data-testid="dc-user-id">{data.user.user_id}</div>
          </div>
          <div>
            <div className="text-xs text-neutral-500">Name</div>
            <div className="text-base">{data.user.full_name}</div>
          </div>
          <div>
            <div className="text-xs text-neutral-500">Package</div>
            <div className="text-base tabular">{data.user.package ? `${data.user.package} ($${data.user.package_value})` : "—"}</div>
          </div>
          <div>
            <div className="text-xs text-neutral-500">Earned so far</div>
            <div className="text-base tabular">${data.user.earnings_so_far.toFixed(2)}</div>
          </div>
          <div>
            <div className="text-xs text-neutral-500">Status</div>
            <div className="text-base">
              {data.user.package_value ? (
                <span className="text-emerald-400 text-sm uppercase tracking-widest">Working</span>
              ) : (
                <span className="text-amber-400 text-sm uppercase tracking-widest">Non-working</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="lt-card p-5 flex items-center justify-between" data-testid="dc-totals-received">
          <div>
            <div className="overline">Donation received (total)</div>
            <div className="font-display text-3xl tabular mt-2 gold-text">${data.totals.received.toFixed(2)}</div>
            <div className="text-xs text-neutral-500 mt-1">{data.totals.received_count} payouts</div>
          </div>
          <ArrowDownToLine className="text-[#D4AF37]/60" size={28} />
        </div>
        <div className="lt-card p-5 flex items-center justify-between" data-testid="dc-totals-sent">
          <div>
            <div className="overline">Donation contributed (total)</div>
            <div className="font-display text-3xl tabular mt-2">${data.totals.sent.toFixed(2)}</div>
            <div className="text-xs text-neutral-500 mt-1">{data.totals.sent_count} contributions</div>
          </div>
          <ArrowUpFromLine className="text-neutral-500" size={28} />
        </div>
      </div>

      <div className="lt-card p-6">
        <div className="overline mb-3">Upline — 5 users who joined BEFORE you (global time order)</div>
        {data.upline.length === 0 ? (
          <div className="text-sm text-neutral-500">No earlier joiners.</div>
        ) : (
          <div className="overflow-x-auto -mx-1">
          <table className="lt-table">
            <thead>
              <tr><th>#</th><th>User ID</th><th>Name</th><th>Email</th><th>Package</th><th>Directs</th><th>Joined</th><th>Status</th></tr>
            </thead>
            <tbody>
              {data.upline.map((u) => (
                <tr key={u.user_id} data-testid={`dc-upline-${u.user_id}`}>
                  <td className="tabular">L{u.level}</td>
                  <td className="tabular">{u.user_id}</td>
                  <td>{u.full_name}</td>
                  <td className="text-neutral-400 text-xs">{u.email}</td>
                  <td className="tabular">{u.package ? `${u.package} ($${u.package_value})` : <span className="text-neutral-500">—</span>}</td>
                  <td className="tabular">{u.directs_count}</td>
                  <td className="text-neutral-400 text-xs">{u.created_at ? new Date(u.created_at).toLocaleString() : "—"}</td>
                  <td className="text-xs uppercase tracking-widest">
                    {u.is_non_working ? <span className="text-amber-400">Non-working</span> : <span className="text-emerald-400">Working</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div className="lt-card p-6">
        <div className="overline mb-3">Downline — 5 users who joined AFTER you (global time order)</div>
        {data.directs.length === 0 ? (
          <div className="text-sm text-neutral-500">No later joiners yet.</div>
        ) : (
          <div className="overflow-x-auto -mx-1">
          <table className="lt-table">
            <thead>
              <tr><th>#</th><th>User ID</th><th>Name</th><th>Joined</th><th>Status</th></tr>
            </thead>
            <tbody>
              {data.directs.map((d) => (
                <tr key={d.user_id} data-testid={`dc-direct-${d.user_id}`}>
                  <td className="tabular">D{d.level}</td>
                  <td className="tabular">{d.user_id}</td>
                  <td>{d.full_name}</td>
                  <td className="text-neutral-400 text-xs">{d.created_at ? new Date(d.created_at).toLocaleString() : "—"}</td>
                  <td className="text-xs uppercase tracking-widest">
                    {d.is_non_working ? <span className="text-amber-400">Non-working</span> : <span className="text-emerald-400">Working</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div className="lt-card p-6">
        <div className="overline mb-3">Donations contributed (sent to others)</div>
        {data.sent.length === 0 ? (
          <div className="text-sm text-neutral-500">No donations contributed yet.</div>
        ) : (
          <div className="overflow-x-auto -mx-1">
          <table className="lt-table">
            <thead>
              <tr><th>When</th><th>To</th><th>Source</th><th className="text-right">Amount</th></tr>
            </thead>
            <tbody>
              {data.sent.map((r) => (
                <tr key={r.id} data-testid={`dc-sent-${r.id}`}>
                  <td className="text-neutral-400">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="tabular">{r.to_no}</td>
                  <td className="text-neutral-400 text-xs">{r.source}</td>
                  <td className="text-right tabular text-neutral-300">-${r.amount.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DonationClub() {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/me/donation-club");
        setData(r.data);
      } catch (e) {
        setMsg(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <div className="overline flex items-center gap-2"><HandCoins size={14} className="text-[#D4AF37]" /> Donation Club</div>
      </div>
      {msg && <div className="text-red-400 text-sm" data-testid="dc-error">{msg}</div>}
      <DonationClubView data={data} />
    </div>
  );
}
